/**
 * The welcome screen's side of the network: signing in and out, creating a world, joining one by
 * its code or its link, leaving. Answers go into the store's `lobby` slice; who you are follows the
 * sign-in; a joined world becomes the current world (and the connection opens).
 */
import { isGuest } from '@bramvasterd/terrella-protocol';

import { logger } from '@core/log';
import { colorFor } from '@core/palette';
import { enterWorld, leaveWorld as leaveCurrent, setMe, type Game } from '@game/game';
import { normalizeCode, setLobby } from '@game/session/lobby';
import type { WorldInfo } from '@game/types';
import { isNative } from '@platform/env';
import { watchLinks } from '@platform/openedLinks';

import { ApiFailure, createCrew, joinCrew, leaveCrew, myWorlds } from './api';
import type { Players } from './guest';

const log = logger('lobby');
const WORLD_KEY = 'world';
/** A world link opened before we could join it (signed out, or still starting), kept for sign-in. */
const JOIN_KEY = 'join';

/** The words the lobby needs from the app's language. */
export interface LobbyText {
  /** The language a guest's name is made in ('en', 'nl'). */
  language(): string;
  /** The name of a new player's first world. */
  firstWorld(name: string): string;
}

export function startLobby(game: Game, auth: Players, text: LobbyText): () => void {
  const { storage } = game.deps;

  const fail = (error: unknown): void => {
    const code = error instanceof ApiFailure ? error.code : 'network';
    log.warn('request failed', code);
    if (code === 'unauthorized') {
      void auth.logout();
      return;
    }
    setLobby(game, { busy: false, error: code });
  };

  const enter = async (world: WorldInfo): Promise<void> => {
    await storage.set(WORLD_KEY, world.id);
    setLobby(game, { busy: false, error: null });
    await enterWorld(game, world);
  };

  const joinLinked = async (id: string): Promise<void> => {
    if (game.store.get().world?.id === id) return;
    setLobby(game, { busy: true, error: null });
    await enter(await joinCrew(id));
  };

  /**
   * A world link opened the app: join it now when signed in. Otherwise keep it for after sign-in
   * (which picks it up); in a browser, once no saved sign-in turned up, become a guest of that
   * world on the spot.
   */
  const opened = (code: string): void => {
    const id = normalizeCode(code);
    if (!id) return;
    if (game.store.get().me) {
      joinLinked(id).catch(fail);
      return;
    }
    const kept = storage.set(JOIN_KEY, id);
    if (isNative) {
      kept.catch(fail);
      return;
    }
    setLobby(game, { busy: true, error: null });
    Promise.all([kept, auth.started()])
      .then(() => (game.store.get().me ? undefined : auth.asGuest(id, text.language())))
      .catch(async (error: unknown) => {
        await storage.remove(JOIN_KEY);
        fail(error);
      });
  };

  /**
   * Signed in: the world a link opened, else the world from last time, else (on a new device) the
   * one I played last; a new player gets a world of their own. A guest only ever joins.
   */
  const arrive = async (): Promise<void> => {
    // someone else now (a guest who signed up) while in a world: join it again as them
    const current = game.store.get().world;
    if (current) {
      await enter(await joinCrew(current.id));
      return;
    }
    const linked = await storage.get(JOIN_KEY);
    if (linked) {
      await storage.remove(JOIN_KEY);
      await joinLinked(linked);
      return;
    }
    setLobby(game, { busy: true, error: null });
    const remembered = await storage.get(WORLD_KEY);
    if (remembered) {
      try {
        await enter(await joinCrew(remembered));
        return;
      } catch (error) {
        await storage.remove(WORLD_KEY);
        if (error instanceof ApiFailure && error.code === 'unauthorized') throw error;
      }
    }
    const me = game.store.get().me;
    if (!me || isGuest(me.id)) {
      setLobby(game, { busy: false, error: null });
      return;
    }
    const [last] = await myWorlds();
    await enter(last ?? (await createCrew(text.firstWorld(me.name))));
  };

  const offAuth = auth.subscribe((state) => {
    if (state.kind === 'busy') {
      setLobby(game, { busy: true, error: null });
      return;
    }
    if (state.kind === 'in') {
      const previous = game.store.get().me;
      const me = {
        id: state.identity.id,
        name: state.identity.name,
        color: colorFor(state.identity.id),
      };
      setMe(game, me);
      const someoneNew = previous?.id !== me.id;
      setLobby(game, { busy: someoneNew, error: null });
      if (someoneNew) arrive().catch(fail);
      return;
    }
    if (game.store.get().world) leaveCurrent(game);
    setMe(game, null);
    setLobby(game, { busy: false, error: state.failed ? 'sign-in-failed' : null });
  });

  const off = game.events.on('lobby', (request) => {
    const run = async (): Promise<void> => {
      switch (request.kind) {
        case 'login':
          await auth.login('login');
          return;
        case 'register':
          await auth.login('register');
          return;
        case 'logout': {
          const world = game.store.get().world;
          await storage.remove(WORLD_KEY);
          if (world) leaveCurrent(game);
          await auth.logout();
          return;
        }
        case 'create':
          setLobby(game, { busy: true, error: null });
          await enter(await createCrew(request.name));
          return;
        case 'join': {
          const code = normalizeCode(request.code);
          if (!code) {
            setLobby(game, { busy: false, error: 'unknown-crew' });
            return;
          }
          setLobby(game, { busy: true, error: null });
          await enter(await joinCrew(code));
          return;
        }
        case 'leave': {
          const world = game.store.get().world;
          leaveCurrent(game);
          await storage.remove(WORLD_KEY);
          if (world) leaveCrew(world.id).catch((error: unknown) => log.warn('leave failed', error));
          setLobby(game, { busy: false, error: null });
          return;
        }
      }
    };
    run().catch(fail);
  });

  // I can't be in my world any more (wiped, or full when a guest came back): forget it, say why
  const offGone = game.events.on('gone', ({ world, reason }) => {
    if (game.store.get().world?.id !== world) return;
    leaveCurrent(game);
    setLobby(game, { busy: false, error: reason });
    storage.remove(WORLD_KEY).catch(fail);
  });

  const offLinks = watchLinks(opened);

  return () => {
    off();
    offAuth();
    offGone();
    offLinks();
  };
}
